import React, { useState, useEffect, useCallback } from 'react';
import { routesAPI } from '@/lib/api';
import { getToday } from '@/lib/utils';
import { 
  Truck, Package, MapPin, Play, Calendar, ChevronRight, 
  CheckCircle2, AlertTriangle, Clock, ArrowLeft 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const LogisticaRoutePage = () => {
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [pendingLocations, setPendingLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadedQuantities, setLoadedQuantities] = useState({});
  const [routeNotes, setRouteNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPendingLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await routesAPI.getPendingByLocation(selectedDate);
      setPendingLocations(response.data);
    } catch (error) {
      toast.error('Error al cargar pedidos pendientes');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadPendingLocations();
  }, [loadPendingLocations]);

  const selectLocation = (location) => {
    setSelectedLocation(location);
    // Inicializar cantidades con lo pedido
    const quantities = {};
    location.items.forEach(item => {
      quantities[item.item_id] = item.requested_quantity;
    });
    setLoadedQuantities(quantities);
    setRouteNotes('');
  };

  const handleQuantityChange = (itemId, value) => {
    setLoadedQuantities(prev => ({
      ...prev,
      [itemId]: parseFloat(value) || 0
    }));
  };

  const getTotalRequested = () => {
    if (!selectedLocation) return 0;
    return selectedLocation.items.reduce((sum, item) => sum + item.requested_quantity, 0);
  };

  const getTotalLoaded = () => {
    return Object.values(loadedQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getCompliancePercentage = () => {
    const requested = getTotalRequested();
    const loaded = getTotalLoaded();
    if (requested === 0) return 100;
    return Math.round((loaded / requested) * 100);
  };

  const startRoute = async () => {
    if (!selectedLocation) return;
    
    setSaving(true);
    try {
      const routeData = {
        location_id: selectedLocation.location_id,
        location_name: selectedLocation.location_name,
        order_ids: selectedLocation.order_ids,
        items: selectedLocation.items.map(item => ({
          item_id: item.item_id,
          item_code: item.item_code,
          item_name: item.item_name,
          unit: item.unit,
          requested_quantity: item.requested_quantity,
          loaded_quantity: loadedQuantities[item.item_id] || 0
        })),
        notes: routeNotes
      };
      
      await routesAPI.create(routeData);
      toast.success(`Ruta iniciada para ${selectedLocation.location_name}`);
      setSelectedLocation(null);
      loadPendingLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar ruta');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tupu-orange-500"></div>
      </div>
    );
  }

  // Vista de detalle de local seleccionado
  if (selectedLocation) {
    return (
      <div className="pb-24 md:pb-6" data-testid="route-detail-view">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedLocation(null)}
            className="mb-2 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-slate-900">Iniciar Ruta</h1>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span className="font-medium text-slate-700">{selectedLocation.location_name}</span>
          </div>
        </div>

        {/* Resumen de cumplimiento */}
        <div className={`rounded-xl p-4 mb-6 ${
          getCompliancePercentage() >= 100 ? 'bg-green-50 border-2 border-green-200' :
          getCompliancePercentage() >= 80 ? 'bg-amber-50 border-2 border-amber-200' : 
          'bg-red-50 border-2 border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-slate-600">Cumplimiento de Pedido</span>
              <p className="text-xs text-slate-500 mt-1">
                {getTotalLoaded()} de {getTotalRequested()} unidades
              </p>
            </div>
            <span className={`text-3xl font-bold ${
              getCompliancePercentage() >= 100 ? 'text-green-600' :
              getCompliancePercentage() >= 80 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {getCompliancePercentage()}%
            </span>
          </div>
        </div>

        {/* Hora actual */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-tupu-orange-500" />
            <div>
              <p className="text-sm text-slate-500">Hora de salida</p>
              <p className="font-bold text-slate-900">
                {format(new Date(), "dd 'de' MMMM, HH:mm", { locale: es })}
              </p>
            </div>
          </div>
        </div>

        {/* Lista de items agrupados por proveedor */}
        {(() => {
          // Agrupar items por proveedor
          const itemsBySupplier = {};
          selectedLocation.items.forEach(item => {
            const supplier = item.supplier || 'Sin proveedor';
            if (!itemsBySupplier[supplier]) {
              itemsBySupplier[supplier] = [];
            }
            itemsBySupplier[supplier].push(item);
          });
          
          const suppliers = Object.keys(itemsBySupplier).sort();
          
          return suppliers.map((supplier, supplierIndex) => (
            <div key={supplier} className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Package className="w-4 h-4 text-tupu-orange-500" />
                    {supplier}
                  </h3>
                  <span className="text-xs bg-tupu-orange-100 text-tupu-orange-700 px-2 py-1 rounded-full font-medium">
                    {itemsBySupplier[supplier].length} producto{itemsBySupplier[supplier].length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {itemsBySupplier[supplier].map((item) => {
                  const loaded = loadedQuantities[item.item_id] || 0;
                  const diff = loaded - item.requested_quantity;
                  
                  return (
                    <div key={item.item_id} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-900">{item.item_name}</p>
                          <p className="text-xs text-slate-500">{item.item_code} • {item.unit}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">
                            Pedido: <span className="font-bold text-slate-900">{item.requested_quantity}</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600 min-w-[60px]">Lleva:</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.1"
                          min="0"
                          value={loadedQuantities[item.item_id] ?? ''}
                          onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                          className="w-24 text-center font-bold"
                          data-testid={`load-qty-${item.item_code}`}
                        />
                        <span className={`text-sm font-medium min-w-[50px] ${
                          diff === 0 ? 'text-green-600' :
                          diff > 0 ? 'text-blue-600' : 'text-red-600'
                        }`}>
                          {diff === 0 ? '✓ OK' : (diff > 0 ? `+${diff}` : diff)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ));
        })()}

        {/* Notas */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <label className="text-sm font-medium text-slate-700 mb-2 block">
            Observaciones (opcional)
          </label>
          <Textarea
            value={routeNotes}
            onChange={(e) => setRouteNotes(e.target.value)}
            placeholder="Notas sobre la ruta o los productos..."
            rows={2}
          />
        </div>

        {/* Botón Iniciar Ruta */}
        <Button 
          onClick={startRoute} 
          disabled={saving}
          className="w-full h-14 text-lg bg-gradient-to-r from-tupu-orange-600 to-tupu-orange-700 hover:from-tupu-orange-700 hover:to-tupu-orange-800"
          data-testid="start-route-btn"
        >
          {saving ? (
            'Iniciando ruta...'
          ) : (
            <>
              <Play className="w-5 h-5 mr-2" />
              Iniciar Ruta a {selectedLocation.location_name}
            </>
          )}
        </Button>
      </div>
    );
  }

  // Vista principal - Lista de locales pendientes
  return (
    <div className="pb-24 md:pb-6" data-testid="logistica-route-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Iniciar Ruta</h1>
        <p className="text-muted-foreground">Selecciona un local para preparar la ruta</p>
      </div>

      {/* Selector de fecha */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-slate-400" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
          />
          <Button variant="outline" onClick={loadPendingLocations}>
            Actualizar
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Locales Pendientes</p>
          <p className="text-2xl font-bold text-amber-600">{pendingLocations.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total Items</p>
          <p className="text-2xl font-bold text-slate-900">
            {pendingLocations.reduce((sum, loc) => sum + loc.total_items, 0)}
          </p>
        </div>
      </div>

      {/* Lista de locales */}
      <div className="space-y-4">
        {pendingLocations.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">¡Todas las rutas iniciadas!</p>
            <p className="text-slate-500 text-sm mt-1">No hay pedidos pendientes para esta fecha</p>
          </div>
        ) : (
          pendingLocations.map((location, index) => (
            <motion.div
              key={location.location_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border-2 border-amber-200 p-4 cursor-pointer transition-all hover:shadow-md hover:border-orange-300"
              onClick={() => selectLocation(location)}
              data-testid={`location-${location.location_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Truck className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <h3 className="font-bold text-slate-900">{location.location_name}</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      {location.total_items} productos • {location.total_quantity} unidades
                    </p>
                    <p className="text-xs text-slate-400">
                      {location.order_ids.length} pedido(s)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-tupu-orange-600 hover:text-tupu-orange-700 hover:bg-tupu-orange-50"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Iniciar
                  </Button>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default LogisticaRoutePage;
